/**
 * levelprimitive.ts — the account's OWN levels (entry / SL / TP / LIQ / resting
 * orders), drawn with labels.
 *
 * WHAT THIS REPLACES AND WHY
 * --------------------------
 * These levels used to be `candleSeries.createPriceLine(...)` calls. That API can
 * only show its `title` in the PRICE-AXIS label, and the axis label is switched off
 * on purpose — with it on, a dozen CE levels plus the range levels stack into a
 * column that covers recent price action. Net effect, reported by Andrei against
 * the live xyz:TSLA bracket: "I see only red and blue lines and no indication
 * what's SL, entry or TP". The prices were right; the identities were invisible.
 *
 * A canvas primitive can put the text ON the chart next to the line, which is what
 * every trading UI does and what the ticket's draggable legs already did.
 *
 * DRAGGABLE SINCE 2026-08-18 (card t_35eac373), PER LEVEL, DEFAULT OFF. Andrei: *"I'd
 * like to have an ability to move SL/TP on all panes like it is done at Hyperliquid."*
 * A level opts in by carrying an `id` — `levelsForPosition` sets one on the live
 * position's SL and TP and on nothing else, so entry, liquidation and every resting
 * order stay exactly as read-only as before. The gesture is `legdrag.LegDrag`, the SAME
 * object `TicketPrimitive` drives, so there is no second answer to "which line is under
 * the cursor and what price is that pixel".
 *
 * A DRAG REPORTS A PRICE AND NOTHING ELSE. It does not write, does not validate and
 * does not clamp: a drag is a gesture, not an intent (`ticket.ts:251`). The owner
 * stages it in the shared model (`livelevels.ts`) and the confirm/refuse rules live
 * where the risk gates are.
 *
 * THE PIXEL IS CONVERTED BY THIS PANE'S OWN SERIES. Panes hold independent price
 * windows (`main.ts:419`), so a stop dragged on 15m and the same stop redrawn on 1W are
 * one price through two different scales — never one shared scale.
 *
 * The label plate itself is `levelplate.drawPlate`, shared with the ticket rather
 * than copied, so collision behaviour cannot drift between the two.
 */
import type {
  IChartApi,
  ISeriesPrimitive,
  ISeriesPrimitivePaneRenderer,
  ISeriesPrimitivePaneView,
  ISeriesApi,
  PrimitiveHoveredItem,
  SeriesAttachedParameter,
  SeriesType,
  Time,
} from 'lightweight-charts';

import { drawPlate, drawRule, type PlateBox } from './levelplate';
import { LegDrag, wireLegDrag } from './legdrag';

/** Bitmap-space drawing target handed to us by lightweight-charts. */
interface Target {
  useBitmapCoordinateSpace(
    cb: (scope: {
      context: CanvasRenderingContext2D;
      horizontalPixelRatio: number;
      verticalPixelRatio: number;
      bitmapSize: { width: number; height: number };
    }) => void,
  ): void;
}

/** One horizontal level with the text that identifies it. */
export interface AccountLevel {
  price: number;
  color: string;
  /** What this level IS — "entry long", "SL 0.808", "Limit 0.808". */
  label: string;
  dashed: boolean;
  /**
   * Drag handle. Absent = read-only, which is every level this file drew before
   * 2026-08-18 and still most of them.
   *
   * IT CARRIES THE POSITION IT BELONGS TO (`livelevels` mints `xyz:AMZN|sl`), so the
   * drag reports the leg it was DRAWN for rather than whatever row is hovered or
   * whichever line happens to be nearest. Andrei runs 26+ isolated-margin positions
   * with lines that sit close together; a stop moved on the wrong instrument is a
   * real loss, and it would not look broken.
   */
  id?: string;
}

export type LevelDragCallbacks = {
  /** Fires on every pointer-move of a drag. `id` is the level's own `id`. */
  onDrag: (id: string, price: number) => void;
  /** Fires once on release. STILL NOT A WRITE — see the file header. */
  onDrop?: (id: string, price: number) => void;
};

export class LevelPrimitive implements ISeriesPrimitive<Time> {
  private _chart: IChartApi | null = null;
  private _series: ISeriesApi<SeriesType> | null = null;
  private _requestUpdate: (() => void) | null = null;
  private _levels: AccountLevel[] = [];
  private _views: ISeriesPrimitivePaneView[];
  private _cb: LevelDragCallbacks | null = null;
  private _detach: (() => void) | null = null;
  /** The chart's own pan flag, saved at grab time and restored on release.
   *
   *  PER PRIMITIVE, and safe only because `legdrag`'s hub gives a gesture to exactly ONE host
   *  (R4-8): this and `TicketPrimitive._savedScroll` both live on the execution pane, and when
   *  both hosts grabbed one pointerdown the second saved the `false` the first had just written
   *  and restored THAT — the chart stopped panning, silently. Only the active host's copy is
   *  ever written now, so these are two charts' flags rather than two states of one. */
  private _savedScroll: unknown = null;
  private readonly _drag = new LegDrag({
    series: () => this._series,
    // Only an `id`-carrying level is grabbable, and only once someone is listening:
    // a level that follows the cursor while nothing records the new price would be a
    // stop that appears to move and does not.
    legs: () =>
      this._cb === null
        ? []
        : this._levels.map((lv) => ({ id: lv.id ?? '', price: lv.price, draggable: Boolean(lv.id) })),
    callbacks: () => this._cb,
    setScroll: (on) => {
      const chart = this._chart;
      if (!chart) return;
      if (!on) {
        this._savedScroll = chart.options().handleScroll;
        chart.applyOptions({ handleScroll: false, handleScale: false });
      } else {
        chart.applyOptions({ handleScroll: (this._savedScroll ?? true) as never, handleScale: true });
      }
    },
    requestUpdate: () => this._requestUpdate?.(),
  });

  constructor() {
    this._views = [
      {
        renderer: (): ISeriesPrimitivePaneRenderer => ({
          draw: (t) => this._draw(t as unknown as Target),
        }),
      },
    ];
  }

  attached(param: SeriesAttachedParameter<Time, SeriesType>): void {
    this._chart = param.chart;
    this._series = param.series;
    this._requestUpdate = param.requestUpdate;
    // Pointer listeners are installed ONCE per pane here, mirroring `TicketPrimitive`:
    // a primitive re-attached per refresh would stack handlers and one drag would move
    // the level twice (`chartpane.ts:697`). `ChartPane` attaches this in its
    // constructor, so that ordering is already guaranteed.
    this._detach = wireLegDrag(param.chart.chartElement(), this._drag);
  }

  detached(): void {
    this._detach?.();
    this._detach = null;
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  paneViews(): readonly ISeriesPrimitivePaneView[] {
    return this._views;
  }

  /** Opt this pane's `id`-carrying levels into dragging. Never called = read-only. */
  setCallbacks(cb: LevelDragCallbacks | null): void {
    this._cb = cb;
  }

  /** Lets the LIBRARY decide what the cursor is over, in the coordinate system it drew
   *  with. The `ns-resize` cursor is the only thing that says a line can be moved. */
  hitTest(x: number, y: number): PrimitiveHoveredItem | null {
    void x; // lines span the pane; proximity in y is the whole test
    const id = this._drag.hover(y);
    return id === null ? null : { cursorStyle: 'ns-resize', externalId: id, zOrder: 'top' };
  }

  /** Replace the levels. `[]` clears them. */
  setData(levels: AccountLevel[]): void {
    this._levels = levels;
    this._requestUpdate?.();
  }

  /** The price this pane is DRAWING for a leg, or null when it draws none.
   *
   *  Exists so cross-pane agreement can be MEASURED rather than assumed: four panes
   *  showing one stop is the whole claim of this feature, and reading it off the
   *  drawn list is the only way to check the pane's own render agrees with the model. */
  priceOfLeg(leg: 'sl' | 'tp'): number | null {
    const hit = this._levels.find((lv) => lv.id?.endsWith(`|${leg}`));
    return hit ? hit.price : null;
  }

  /** The labels this pane is DRAWING, in order.
   *
   *  Also for measurement, and for the same reason `priceOfLeg` exists: after redteam D1
   *  (two panes drawing two different stops, both labelled `SL`) the claim "the plan and
   *  the resting order are distinguishable" has to be READ off the pane rather than
   *  assumed from the code that built the list. */
  drawnLabels(): string[] {
    return this._levels.map((lv) => lv.label);
  }

  /** Drag a leg to a PIXEL on this pane, through this pane's own price scale.
   *
   *  The harness lever for the drag: lightweight-charts' hit testing ignores synthetic
   *  pointer events aimed at the canvas, so a test cannot reach `hitTest`. This calls the
   *  SAME conversion and the SAME callback the pointer handler does — a parallel
   *  implementation here would prove nothing about production.
   *
   *  `release` ALSO fires `onDrop`, i.e. the whole gesture including mouse-up, for the case
   *  a real pointer cannot reach: a level that is OFF the pane's visible range (a position
   *  deep in profit puts its far TP at a negative y, measured -52 on a 566px pane), which
   *  cannot be pressed and must not be made pressable by scrolling the chart for the
   *  benefit of the test. The pointer half of the gesture is witnessed separately by
   *  `chartapp/e2e/legdrag.spec.ts`, whose first mutant removes the `pointerdown` binding.
   *
   *  Returns the price it staged, or null when this pane draws no such leg / the pixel
   *  maps to no usable price. */
  dragToCoordinate(leg: 'sl' | 'tp', y: number, release = false): number | null {
    const hit = this._levels.find((lv) => lv.id?.endsWith(`|${leg}`));
    const series = this._series;
    if (!hit?.id || !series || !this._cb) return null;
    const price = Number(series.coordinateToPrice(y));
    if (!Number.isFinite(price) || price <= 0) return null;
    this._cb.onDrag(hit.id, price);
    if (release) this._cb.onDrop?.(hit.id, price);
    return price;
  }

  private _y(price: number, vr: number): number | null {
    const c = this._series?.priceToCoordinate(price);
    return c === null || c === undefined ? null : c * vr;
  }

  private _draw(target: Target): void {
    if (!this._series) return;
    target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hr,
                                     verticalPixelRatio: vr, bitmapSize }) => {
      // y ORDER matters: `drawPlate` steps a colliding plate to the right, and its
      // single-pass check is only correct when earlier plates are already placed.
      const ordered = this._levels
        .map((lv) => ({ lv, y: this._y(lv.price, vr) }))
        .filter((r): r is { lv: AccountLevel; y: number } => r.y !== null)
        .sort((a, b) => a.y - b.y);

      const taken: PlateBox[] = [];
      const paneHeight = bitmapSize.height / vr;
      for (const { lv, y } of ordered) {
        // Emphasis on the grabbed/hovered line, exactly as the ticket's legs do it:
        // without it a drag gives no feedback about WHICH line moved.
        const active = lv.id !== undefined && (this._drag.dragging === lv.id || this._drag.hovered === lv.id);
        drawRule(ctx, y, bitmapSize.width, lv.color, hr, { dashed: lv.dashed, emphasis: active });
        taken.push(drawPlate(ctx, lv.label, lv.color, y, hr, vr, taken, { paneHeight, emphasis: active }));
      }
    });
  }
}
